import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as api from "../api/disciplio-api";
import { useBrowserStorageConsent } from "../context/browser-storage-consent-context";
import { useDisciplio } from "../context/disciplio-context";
import { useI18n } from "../context/i18n-context";
import { capturePageView, identifyUser, resetAnalytics } from "./index";
import { sanitizePathname } from "./url";

/**
 * Rendered once inside BrowserRouter and inside DisciplioProvider (main.tsx).
 * Owns the two pieces of analytics wiring that need live app state:
 *  - SPA pageview capture on route change (pathname only).
 *  - Identifying the authenticated user once consent, session, and remote
 *    profile data are all ready.
 * Renders nothing.
 */
export function AnalyticsBridge() {
  const location = useLocation();
  const { isOptionalStorageEnabled } = useBrowserStorageConsent();
  const { state, isRemoteHydrated, isSessionReady } = useDisciplio();
  const { language } = useI18n();

  const lastPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOptionalStorageEnabled) return;
    const pathname = sanitizePathname(location.pathname);
    if (lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;
    capturePageView(pathname);
  }, [isOptionalStorageEnabled, location.pathname]);

  const identifiedUserKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOptionalStorageEnabled || !api.hasApiSession()) {
      identifiedUserKeyRef.current = null;
      return;
    }
    if (!isSessionReady || !isRemoteHydrated) return;
    const userId = state.profile.userId;
    if (!userId) return;
    const role = state.profile.accountRole || "UNKNOWN";
    const identityKey = `${userId}:${role}`;
    if (identifiedUserKeyRef.current === identityKey) return;
    if (
      identifiedUserKeyRef.current?.startsWith(`${userId}:`) &&
      identifiedUserKeyRef.current !== identityKey
    ) {
      resetAnalytics();
    }
    identifiedUserKeyRef.current = identityKey;
    identifyUser(userId, {
      language,
      account_role: state.profile.accountRole,
      onboarding_completed: state.profile.onboardingCompleted,
    });
  }, [
    isOptionalStorageEnabled,
    isRemoteHydrated,
    isSessionReady,
    language,
    state.profile.accountRole,
    state.profile.onboardingCompleted,
    state.profile.userId,
  ]);

  return null;
}
